<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import PageLayout from 'dashboard/components-next/agents/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentCard from 'dashboard/components-next/agents/AgentCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AgentsAPI from 'dashboard/api/agents/agents';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const uiFlags = useMapGetter('aiAgents/getUIFlags');
const agents = useMapGetter('aiAgents/getRecords');

const isFetching = computed(() => uiFlags.value.fetchingList);
const deletingAgentId = ref(null);

const accountId = computed(() => route.params.accountId);

// Follow-up automático NATIVO (Agno::ReengagementJob): a persona do agente
// escreve o texto do retorno. Config em agent.config.follow_up (payload
// do index traz `follow_up`); o modal aplica a mesma cadência a todos.
const followUpSaving = ref(false);
const followUpOn = computed(() =>
  agents.value.some(a => a.follow_up?.enabled === true)
);

// Modal de configuração: ligar/desligar + cadência (minutos antes de cada
// follow-up) + encerrar ao final. Salva o MESMO config em todos os agentes
// agno da conta; o Agno::ReengagementJob lê steps_minutes/resolve_at_end.
const followUpDialog = ref(null);
const followUpForm = ref({ enabled: false, steps: [], resolveAtEnd: true });
const customAmount = ref(null);
const customUnit = ref('minutes');

const FOLLOW_UP_PRESETS = [10, 30, 60, 180, 1440, 10080, 20160, 30240];
const DEFAULT_STEPS = [10, 30, 60, 1440, 10080, 20160, 30240];

const formatOffset = minutes => {
  if (minutes % 10080 === 0)
    return t('AI_AGENTS.FOLLOW_UP.WEEKS', { count: minutes / 10080 });
  if (minutes % 1440 === 0)
    return t('AI_AGENTS.FOLLOW_UP.DAYS', { count: minutes / 1440 });
  if (minutes % 60 === 0)
    return t('AI_AGENTS.FOLLOW_UP.HOURS', { count: minutes / 60 });
  return t('AI_AGENTS.FOLLOW_UP.MINUTES', { count: minutes });
};

const openFollowUpConfig = () => {
  const config = agents.value.find(a => a.follow_up)?.follow_up || {};
  const steps = Array.isArray(config.steps_minutes)
    ? config.steps_minutes.map(Number).filter(n => n > 0)
    : [];
  followUpForm.value = {
    enabled: config.enabled === true,
    steps: steps.length ? [...steps].sort((a, b) => a - b) : DEFAULT_STEPS,
    resolveAtEnd: config.resolve_at_end !== false,
    // Último degrau: em vez de mais um follow-up, entrega a conversa.
    // Vazio/zero = desligado, e o job segue exatamente como antes.
    escalateAfter: Number(config.escalate_after_minutes) || null,
    escalateKind: config.escalate_to?.kind || 'sdr',
    escalateId: config.escalate_to?.id || null,
    sdrPool: Array.isArray(config.sdr_pool_user_ids)
      ? config.sdr_pool_user_ids.map(Number)
      : [],
  };
  followUpDialog.value?.open();
};

// Destinos possíveis do escalonamento. Equipes e atendentes vêm dos módulos
// da conta; "outro agente de IA" sai da própria lista de agentes agno.
const teams = computed(() => store.getters['teams/getTeams'] || []);
const accountUsers = computed(() => store.getters['agents/getAgents'] || []);
// A config é gravada em TODOS os agentes agno, então um agente poderia acabar
// escalando para si mesmo — o job ignora esse caso, mas nem oferecemos.
const escalateNeedsTarget = computed(() =>
  ['team', 'agent'].includes(followUpForm.value.escalateKind)
);

const onEscalateKind = () => {
  // trocar o tipo de destino invalida o id do tipo anterior
  followUpForm.value.escalateId = null;
};

const toggleSdrPoolUser = (id, checked) => {
  const pool = followUpForm.value.sdrPool.filter(x => x !== Number(id));
  if (checked) pool.push(Number(id));
  followUpForm.value.sdrPool = pool;
};

const toggleStep = minutes => {
  const steps = followUpForm.value.steps;
  followUpForm.value.steps = steps.includes(minutes)
    ? steps.filter(m => m !== minutes)
    : [...steps, minutes].sort((a, b) => a - b);
};

const addCustomStep = () => {
  const amount = Number(customAmount.value);
  if (!amount || amount <= 0) return;
  const perUnit = { minutes: 1, hours: 60, days: 1440 };
  const minutes = Math.round(amount * (perUnit[customUnit.value] || 1));
  if (!followUpForm.value.steps.includes(minutes)) toggleStep(minutes);
  customAmount.value = null;
};

const saveFollowUp = async () => {
  if (followUpForm.value.enabled && !followUpForm.value.steps.length) {
    useAlert(t('AI_AGENTS.FOLLOW_UP.NEED_STEPS'));
    return;
  }
  // Salvar "escalar para uma equipe" sem dizer QUAL equipe gravava um destino
  // impossível de resolver: o job não escalava e ninguém ficava sabendo.
  if (
    followUpForm.value.escalateAfter &&
    escalateNeedsTarget.value &&
    !followUpForm.value.escalateId
  ) {
    useAlert(t('AI_AGENTS.FOLLOW_UP.ESCALATE_NEED_TARGET'));
    return;
  }
  followUpSaving.value = true;
  try {
    await Promise.all(
      // AgentsAPI.update recebe UM objeto e desestrutura `{ id, ...data }`;
      // passar (id, data) em dois args deixava o id undefined → PUT
      // /agno/agents/undefined 404 (o follow-up nunca salvava).
      agents.value
        .filter(agent => agent.id)
        .map(agent =>
          AgentsAPI.update({
            id: agent.id,
            config: {
              follow_up: {
                enabled: followUpForm.value.enabled,
                steps_minutes: [...followUpForm.value.steps],
                resolve_at_end: followUpForm.value.resolveAtEnd,
                // Sem minutos = chave nula: o job trata como desligado e o
                // comportamento de hoje fica intacto.
                escalate_after_minutes:
                  followUpForm.value.escalateAfter || null,
                escalate_to: followUpForm.value.escalateAfter
                  ? {
                      kind: followUpForm.value.escalateKind,
                      id: followUpForm.value.escalateId || null,
                    }
                  : null,
                sdr_pool_user_ids: [...followUpForm.value.sdrPool],
              },
            },
          })
        )
    );
    await store.dispatch('aiAgents/get');
    followUpDialog.value?.close();
    useAlert(
      followUpForm.value.enabled
        ? t('AI_AGENTS.FOLLOW_UP.ENABLED')
        : t('AI_AGENTS.FOLLOW_UP.DISABLED')
    );
  } catch (error) {
    useAlert(t('AI_AGENTS.FOLLOW_UP.ERROR'));
  } finally {
    followUpSaving.value = false;
  }
};

// --- Painel de Qualidade (dialog por agente) ---
const qualityDialog = ref(null);
const qualityAgent = ref(null);
const qualityData = ref(null);
const qualityLoading = ref(false);

const supervisorSaving = ref(false);

const toggleSupervisor = async () => {
  if (!qualityAgent.value?.id) return;
  const enabling = qualityAgent.value.supervisor?.enabled !== true;
  supervisorSaving.value = true;
  try {
    // Idem saveFollowUp: um objeto só, com o id dentro. E o merge de `config`
    // no backend é RASO — reenviar o supervisor inteiro pra não apagar
    // `topics` (lido por Agno::SupervisorService).
    await AgentsAPI.update({
      id: qualityAgent.value.id,
      config: {
        supervisor: {
          ...(qualityAgent.value.supervisor || {}),
          enabled: enabling,
        },
      },
    });
    qualityAgent.value = {
      ...qualityAgent.value,
      supervisor: {
        ...(qualityAgent.value.supervisor || {}),
        enabled: enabling,
      },
    };
    await store.dispatch('aiAgents/get');
    useAlert(
      enabling
        ? t('AI_AGENTS.SUPERVISOR.ENABLED')
        : t('AI_AGENTS.SUPERVISOR.DISABLED')
    );
  } catch (error) {
    useAlert(t('AI_AGENTS.SUPERVISOR.ERROR'));
  } finally {
    supervisorSaving.value = false;
  }
};

// Nomes comerciais das ferramentas (A3 — explicabilidade)
const TOOL_LABELS = {
  agendar_reuniao: 'Agendou reunião',
  aplicar_etiqueta: 'Etiquetou',
  escalar_humano: 'Escalou para humano',
  resolver_conversa: 'Resolveu a conversa',
  buscar_kb: 'Consultou a base',
  consultar_catalogo: 'Consultou o catálogo',
  dados_empresa: 'Consultou dados da empresa',
  responder_em_audio: 'Respondeu em áudio',
  nota_interna: 'Deixou nota interna',
  capturar_lead: 'Capturou o lead',
};
const toolLabel = tool => TOOL_LABELS[tool] || tool;

// A2 — testes dourados (CI de prompt) do agente aberto no dialog
const goldenTests = ref([]);
const goldenForm = ref({ name: '', user_message: '', expectation: '' });
const goldenSaving = ref(false);

const fetchGoldenTests = async agentId => {
  try {
    const { data } = await AgentsAPI.goldenTests(agentId);
    goldenTests.value = data;
  } catch (error) {
    goldenTests.value = [];
  }
};

const addGoldenTest = async () => {
  const form = goldenForm.value;
  if (!form.user_message.trim() || !form.expectation.trim()) return;
  goldenSaving.value = true;
  try {
    await AgentsAPI.createGoldenTest(qualityAgent.value.id, {
      name: form.name.trim() || form.user_message.trim().slice(0, 40),
      user_message: form.user_message.trim(),
      expectation: form.expectation.trim(),
    });
    goldenForm.value = { name: '', user_message: '', expectation: '' };
    await fetchGoldenTests(qualityAgent.value.id);
    useAlert(t('AI_AGENTS.GOLDEN.ADDED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.GOLDEN.ERROR'));
  } finally {
    goldenSaving.value = false;
  }
};

const removeGoldenTest = async test => {
  try {
    await AgentsAPI.deleteGoldenTest(qualityAgent.value.id, test.id);
    goldenTests.value = goldenTests.value.filter(item => item.id !== test.id);
  } catch (error) {
    useAlert(t('AI_AGENTS.GOLDEN.ERROR'));
  }
};

const runGoldenTests = async () => {
  try {
    await AgentsAPI.runGoldenTests(qualityAgent.value.id);
    useAlert(t('AI_AGENTS.GOLDEN.QUEUED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.GOLDEN.ERROR'));
  }
};

// Loop de autocorreção — sugestões do PromptDoctor (aprovação humana)
const promptSuggestions = ref([]);
const doctorRunning = ref(false);

const fetchPromptSuggestions = async agentId => {
  try {
    const { data } = await AgentsAPI.promptSuggestions(agentId);
    promptSuggestions.value = data;
  } catch (error) {
    promptSuggestions.value = [];
  }
};

const approveSuggestion = async suggestion => {
  try {
    await AgentsAPI.approvePromptSuggestion(
      qualityAgent.value.id,
      suggestion.id
    );
    suggestion.status = 'approved';
    useAlert(t('AI_AGENTS.DOCTOR.APPROVED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.DOCTOR.ERROR'));
  }
};

const rejectSuggestion = async suggestion => {
  try {
    await AgentsAPI.rejectPromptSuggestion(
      qualityAgent.value.id,
      suggestion.id
    );
    suggestion.status = 'rejected';
  } catch (error) {
    useAlert(t('AI_AGENTS.DOCTOR.ERROR'));
  }
};

const runDoctorNow = async () => {
  doctorRunning.value = true;
  try {
    const { data } = await AgentsAPI.runPromptDoctor(qualityAgent.value.id);
    if (data.id) {
      await fetchPromptSuggestions(qualityAgent.value.id);
      useAlert(t('AI_AGENTS.DOCTOR.CREATED'));
    } else {
      useAlert(t('AI_AGENTS.DOCTOR.NOTHING'));
    }
  } catch (error) {
    useAlert(t('AI_AGENTS.DOCTOR.ERROR'));
  } finally {
    doctorRunning.value = false;
  }
};

const handleQuality = async agent => {
  qualityAgent.value = agent;
  qualityData.value = null;
  qualityDialog.value?.open();
  qualityLoading.value = true;
  fetchGoldenTests(agent.id);
  fetchPromptSuggestions(agent.id);
  try {
    const { data } = await AgentsAPI.quality(agent.id, 7);
    qualityData.value = data;
  } catch (error) {
    useAlert(t('AI_AGENTS.QUALITY.LOAD_ERROR'));
  } finally {
    qualityLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('aiAgents/get');
  store.dispatch('aiAgents/getTiers');
  // Destinos do escalonamento por inatividade (equipe / atendente).
  store.dispatch('teams/get');
  store.dispatch('agents/get');
});

const handleCreate = () => {
  router.push({
    name: 'ai_agents_create',
    params: { accountId: accountId.value },
  });
};

const handleCardClick = agent => {
  router.push({
    name: 'ai_agents_flow',
    params: { accountId: accountId.value, agentId: agent.id },
  });
};

const handleRules = agent => {
  router.push({
    name: 'ai_agents_rules',
    params: { accountId: accountId.value, agentId: agent.id },
  });
};

const handleFlow = agent => {
  router.push({
    name: 'ai_agents_flow',
    params: { accountId: accountId.value, agentId: agent.id },
  });
};

const handleDelete = async agent => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('AI_AGENTS.CONFIRM_DELETE', { name: agent.name }))) {
    return;
  }
  try {
    deletingAgentId.value = agent.id;
    await store.dispatch('aiAgents/delete', agent.id);
    useAlert(t('AI_AGENTS.MESSAGES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.MESSAGES.DELETE_ERROR'));
  } finally {
    deletingAgentId.value = null;
  }
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    full-width
  >
    <template #header>
      <div class="flex items-center justify-between w-full gap-4 flex-wrap">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('AI_AGENTS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('AI_AGENTS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <Button
            v-if="agents.length"
            variant="outline"
            :color="followUpOn ? 'teal' : 'slate'"
            icon="i-lucide-alarm-clock"
            :label="
              followUpOn
                ? t('AI_AGENTS.FOLLOW_UP.ON')
                : t('AI_AGENTS.FOLLOW_UP.OFF')
            "
            :is-loading="followUpSaving"
            @click="openFollowUpConfig"
          />
          <Button
            :label="t('AI_AGENTS.ACTIONS.CREATE')"
            icon="i-lucide-plus"
            @click="handleCreate"
          />
        </div>
      </div>
    </template>

    <template #body>
      <div class="pb-4">
        <!-- Empty State -->
        <div
          v-if="!isFetching && agents.length === 0"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div
            class="w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-n-brand/20 to-n-brand/5 flex items-center justify-center"
          >
            <span class="i-lucide-bot text-4xl text-n-brand" />
          </div>
          <h3 class="text-xl font-semibold text-n-slate-12 mb-2">
            {{ t('AI_AGENTS.EMPTY.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-8 max-w-md">
            {{ t('AI_AGENTS.EMPTY.DESCRIPTION') }}
          </p>
          <Button
            :label="t('AI_AGENTS.EMPTY.CREATE_FIRST')"
            icon="i-lucide-plus"
            size="lg"
            @click="handleCreate"
          />
        </div>

        <!-- Agents Grid -->
        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4"
        >
          <AgentCard
            v-for="agent in agents"
            :key="agent.id"
            :agent="agent"
            @click="handleCardClick"
            @delete="handleDelete"
            @rules="handleRules"
            @quality="handleQuality"
            @flow="handleFlow"
          />

          <!-- Create New Card -->
          <div
            class="border-2 border-dashed border-n-weak rounded-2xl p-5 flex flex-col items-center justify-center min-h-[200px] hover:border-n-brand hover:bg-n-alpha-1 transition-all cursor-pointer group"
            @click="handleCreate"
          >
            <div
              class="w-14 h-14 rounded-xl bg-n-alpha-2 group-hover:bg-n-brand/10 flex items-center justify-center mb-4 transition-colors"
            >
              <span
                class="i-lucide-plus text-2xl text-n-slate-10 group-hover:text-n-brand transition-colors"
              />
            </div>
            <span
              class="text-sm font-medium text-n-slate-11 group-hover:text-n-brand transition-colors"
            >
              {{ t('AI_AGENTS.ACTIONS.CREATE_NEW') }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>

  <!-- Painel de Qualidade do agente (LLM-juiz + operações, 7 dias) -->
  <Dialog
    ref="qualityDialog"
    type="alert"
    overflow-y-auto
    :title="`${t('AI_AGENTS.QUALITY.TITLE')} — ${qualityAgent?.name || ''}`"
    :confirm-button-label="t('AI_AGENTS.QUALITY.CLOSE')"
    @confirm="qualityDialog?.close()"
  >
    <div v-if="qualityLoading" class="py-6 text-center text-sm text-n-slate-11">
      {{ t('AI_AGENTS.QUALITY.LOADING') }}
    </div>
    <div v-else-if="qualityData" class="flex flex-col gap-4 text-sm">
      <!-- Meta de negócio: desfecho, não só qualidade da resposta -->
      <div
        v-if="qualityData.meta"
        class="rounded-lg border border-n-teal-5 bg-n-teal-2 p-3"
      >
        <div class="flex items-center justify-between gap-2">
          <div>
            <p class="text-[11px] font-semibold uppercase text-n-teal-11">
              {{ t('AI_AGENTS.QUALITY.META_TITLE') }}
            </p>
            <p class="text-sm text-n-slate-12">{{ qualityData.meta.label }}</p>
          </div>
          <div class="text-right">
            <p class="text-2xl font-bold text-n-teal-11">
              {{ qualityData.meta.rate }}%
            </p>
            <p class="text-[11px] text-n-slate-10">
              {{
                t('AI_AGENTS.QUALITY.META_RATIO', {
                  converted: qualityData.meta.converted,
                  total: qualityData.meta.conversations,
                })
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Notas do juiz -->
      <div v-if="qualityData.evaluations.count" class="grid grid-cols-3 gap-2">
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-center"
        >
          <p class="text-2xl font-bold text-n-slate-12">
            {{ qualityData.evaluations.avg_score }}
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.QUALITY.AVG_SCORE') }}
          </p>
        </div>
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-center"
        >
          <p class="text-2xl font-bold text-n-slate-12">
            {{ qualityData.evaluations.avg_tone }}
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.QUALITY.TONE') }}
          </p>
        </div>
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-center"
        >
          <p
            class="text-2xl font-bold"
            :class="
              qualityData.evaluations.hallucination_count
                ? 'text-n-ruby-11'
                : 'text-n-teal-11'
            "
          >
            {{ qualityData.evaluations.hallucination_count }}
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.QUALITY.HALLUCINATIONS') }}
          </p>
        </div>
      </div>
      <p v-else class="rounded-lg bg-n-alpha-1 p-3 text-xs text-n-slate-11">
        {{ t('AI_AGENTS.QUALITY.NO_EVALS') }}
      </p>

      <!-- Operações -->
      <div class="grid grid-cols-3 gap-2">
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-center"
        >
          <p class="text-2xl font-bold text-n-slate-12">
            {{ qualityData.operations.conversations || 0 }}
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.QUALITY.CONVERSATIONS') }}
          </p>
        </div>
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-center"
        >
          <p class="text-2xl font-bold text-n-teal-11">
            {{ qualityData.operations.autonomy_rate || 0 }}%
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.QUALITY.AUTONOMY') }}
          </p>
        </div>
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-center"
        >
          <p class="text-2xl font-bold text-n-slate-12">
            {{ qualityData.operations.handed_to_human || 0 }}
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.QUALITY.HANDED') }}
          </p>
        </div>
      </div>

      <!-- Supervisor de respostas (C3): modo aprovação para temas de risco -->
      <label
        class="flex items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3"
      >
        <span class="min-w-0">
          <span class="block text-xs font-medium text-n-slate-12">
            {{ t('AI_AGENTS.SUPERVISOR.TITLE') }}
          </span>
          <span class="block text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.SUPERVISOR.DESC') }}
          </span>
        </span>
        <input
          type="checkbox"
          class="rounded border-n-weak"
          :checked="qualityAgent?.supervisor?.enabled === true"
          :disabled="supervisorSaving"
          @change="toggleSupervisor"
        />
      </label>

      <!-- Decisões recentes (A3): o que o agente FEZ, auditável -->
      <div v-if="qualityData.decisions?.length">
        <p class="mb-1.5 text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.QUALITY.DECISIONS') }}
        </p>
        <div class="flex flex-col gap-1">
          <div
            v-for="(decision, index) in qualityData.decisions"
            :key="index"
            class="flex flex-wrap items-center gap-1.5 rounded-lg bg-n-alpha-1 px-2 py-1 text-xs"
          >
            <router-link
              :to="`/app/accounts/${accountId}/conversations/${decision.display_id}`"
              class="font-medium text-n-blue-11 hover:underline"
              @click="qualityDialog?.close()"
            >
              #{{ decision.display_id }}
            </router-link>
            <span
              v-for="tool in decision.tools"
              :key="tool"
              class="rounded-full bg-n-teal-3 px-1.5 py-0.5 text-[10px] text-n-teal-11"
            >
              {{ toolLabel(tool) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Testes dourados (A2 — CI de prompt) -->
      <div>
        <!-- Treinador do agente (loop de autocorreção — aprovação humana) -->
        <div class="mb-3 rounded-xl border border-n-violet-6 bg-n-violet-2 p-3">
          <div class="mb-1 flex items-center justify-between">
            <p class="text-xs font-semibold text-n-violet-11">
              {{ t('AI_AGENTS.DOCTOR.TITLE') }}
            </p>
            <Button
              link
              xs
              icon="i-lucide-stethoscope"
              :label="t('AI_AGENTS.DOCTOR.RUN')"
              :is-loading="doctorRunning"
              @click="runDoctorNow"
            />
          </div>
          <p
            v-if="!promptSuggestions.length"
            class="text-[11px] text-n-slate-10"
          >
            {{ t('AI_AGENTS.DOCTOR.EMPTY') }}
          </p>
          <div
            v-for="suggestion in promptSuggestions"
            :key="suggestion.id"
            class="mb-2 rounded-lg bg-n-solid-1 p-2.5 text-xs"
          >
            <p class="font-medium text-n-slate-12">
              {{ suggestion.diagnosis }}
            </p>
            <p
              class="mt-1 whitespace-pre-wrap rounded bg-n-alpha-1 p-2 text-n-slate-11"
            >
              {{ suggestion.rule_text }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-10">
              {{ suggestion.rationale }}
              ·
              {{
                t('AI_AGENTS.DOCTOR.GOLDEN_BADGE', {
                  passed: suggestion.golden_result?.passed || 0,
                  total:
                    (suggestion.golden_result?.passed || 0) +
                    (suggestion.golden_result?.failed || 0),
                })
              }}
              ·
              {{
                t('AI_AGENTS.DOCTOR.EVIDENCE_BADGE', {
                  count: suggestion.evidence?.count || 0,
                  avg: suggestion.evidence?.avg_score || '—',
                })
              }}
            </p>
            <div
              v-if="suggestion.status === 'pending'"
              class="mt-2 flex items-center gap-2"
            >
              <Button
                xs
                icon="i-lucide-check"
                :label="t('AI_AGENTS.DOCTOR.APPROVE')"
                @click="approveSuggestion(suggestion)"
              />
              <Button
                xs
                variant="faded"
                color="slate"
                :label="t('AI_AGENTS.DOCTOR.REJECT')"
                @click="rejectSuggestion(suggestion)"
              />
            </div>
            <span
              v-else
              class="mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
              :class="{
                'bg-n-teal-3 text-n-teal-11': suggestion.status === 'approved',
                'bg-n-amber-3 text-n-amber-11':
                  suggestion.status === 'reverted',
                'bg-n-alpha-2 text-n-slate-11':
                  suggestion.status === 'rejected',
              }"
            >
              {{
                suggestion.status === 'approved'
                  ? t('AI_AGENTS.DOCTOR.STATUS_APPROVED')
                  : suggestion.status === 'reverted'
                    ? t('AI_AGENTS.DOCTOR.STATUS_REVERTED')
                    : t('AI_AGENTS.DOCTOR.STATUS_REJECTED')
              }}
            </span>
          </div>
        </div>

        <div class="mb-1.5 flex items-center justify-between">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.GOLDEN.TITLE') }}
          </p>
          <Button
            v-if="goldenTests.length"
            link
            xs
            icon="i-lucide-play"
            :label="t('AI_AGENTS.GOLDEN.RUN')"
            @click="runGoldenTests"
          />
        </div>
        <div v-if="goldenTests.length" class="mb-2 flex flex-col gap-1">
          <div
            v-for="test in goldenTests"
            :key="test.id"
            class="flex items-start gap-2 rounded-lg bg-n-alpha-1 px-2 py-1.5 text-xs"
          >
            <span
              class="mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
              :class="{
                'bg-n-teal-3 text-n-teal-11': test.last_passed === true,
                'bg-n-ruby-3 text-n-ruby-11': test.last_passed === false,
                'bg-n-alpha-2 text-n-slate-11': test.last_passed === null,
              }"
            >
              {{
                test.last_passed === true
                  ? t('AI_AGENTS.GOLDEN.PASS')
                  : test.last_passed === false
                    ? t('AI_AGENTS.GOLDEN.FAIL')
                    : t('AI_AGENTS.GOLDEN.PENDING')
              }}
            </span>
            <div class="min-w-0 flex-1">
              <p class="font-medium text-n-slate-12">{{ test.name }}</p>
              <p
                v-if="test.last_passed === false && test.last_reason"
                class="text-n-ruby-11"
              >
                {{ test.last_reason }}
              </p>
            </div>
            <button
              type="button"
              class="i-lucide-trash-2 mt-0.5 size-3.5 shrink-0 text-n-slate-10 hover:text-n-ruby-11"
              @click="removeGoldenTest(test)"
            />
          </div>
        </div>
        <div
          class="flex flex-col gap-1.5 rounded-lg border border-dashed border-n-weak p-2"
        >
          <input
            v-model="goldenForm.user_message"
            type="text"
            class="reset-base w-full rounded-md border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
            :placeholder="t('AI_AGENTS.GOLDEN.MESSAGE_PH')"
          />
          <input
            v-model="goldenForm.expectation"
            type="text"
            class="reset-base w-full rounded-md border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12"
            :placeholder="t('AI_AGENTS.GOLDEN.EXPECTATION_PH')"
          />
          <Button
            size="sm"
            variant="faded"
            color="slate"
            icon="i-lucide-plus"
            :label="t('AI_AGENTS.GOLDEN.ADD')"
            :is-loading="goldenSaving"
            @click="addGoldenTest"
          />
        </div>
      </div>

      <!-- Piores conversas -->
      <div v-if="qualityData.evaluations.worst?.length">
        <p class="mb-1.5 text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.QUALITY.WORST') }}
        </p>
        <div class="flex flex-col gap-1.5">
          <div
            v-for="item in qualityData.evaluations.worst"
            :key="item.conversation_id"
            class="flex items-start gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-2 text-xs"
          >
            <span
              class="shrink-0 rounded-full px-1.5 py-0.5 font-semibold"
              :class="
                item.score >= 70
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{ item.score }}
            </span>
            <div class="min-w-0">
              <router-link
                :to="`/app/accounts/${accountId}/conversations/${item.display_id}`"
                class="font-medium text-n-blue-11 hover:underline"
                @click="qualityDialog?.close()"
              >
                #{{ item.display_id }}
              </router-link>
              <p class="text-n-slate-11">{{ item.summary }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>

  <!-- Config do follow-up automático (cadência do Lia::FollowUpJob) -->
  <Dialog
    ref="followUpDialog"
    :title="t('AI_AGENTS.FOLLOW_UP.DIALOG_TITLE')"
    :confirm-button-label="t('AI_AGENTS.FOLLOW_UP.SAVE')"
    :is-loading="followUpSaving"
    @confirm="saveFollowUp"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FOLLOW_UP.DIALOG_DESC') }}
      </p>

      <label class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FOLLOW_UP.ENABLE') }}
        </span>
        <input
          v-model="followUpForm.enabled"
          type="checkbox"
          class="rounded border-n-weak"
        />
      </label>

      <div v-if="followUpForm.enabled" class="flex flex-col gap-3">
        <div>
          <p class="mb-1.5 text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FOLLOW_UP.STEPS_LABEL') }}
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="preset in FOLLOW_UP_PRESETS"
              :key="preset"
              type="button"
              class="rounded-full border px-2.5 py-1 text-xs font-medium transition-colors"
              :class="
                followUpForm.steps.includes(preset)
                  ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                  : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
              "
              @click="toggleStep(preset)"
            >
              {{ formatOffset(preset) }}
            </button>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <input
            v-model.number="customAmount"
            type="number"
            min="1"
            class="reset-base w-24 rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
            :placeholder="t('AI_AGENTS.FOLLOW_UP.CUSTOM_PH')"
            @keydown.enter.prevent="addCustomStep"
          />
          <select
            v-model="customUnit"
            class="reset-base rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          >
            <option value="minutes">
              {{ t('AI_AGENTS.FOLLOW_UP.UNIT_MINUTES') }}
            </option>
            <option value="hours">
              {{ t('AI_AGENTS.FOLLOW_UP.UNIT_HOURS') }}
            </option>
            <option value="days">
              {{ t('AI_AGENTS.FOLLOW_UP.UNIT_DAYS') }}
            </option>
          </select>
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="t('AI_AGENTS.FOLLOW_UP.ADD_CUSTOM')"
            @click="addCustomStep"
          />
        </div>

        <div v-if="followUpForm.steps.length" class="flex flex-wrap gap-1.5">
          <span
            v-for="minutes in followUpForm.steps"
            :key="minutes"
            class="inline-flex items-center gap-1 rounded-full border border-n-teal-8 bg-n-teal-3 px-2 py-0.5 text-xs text-n-teal-11"
          >
            {{ formatOffset(minutes) }}
            <button type="button" @click="toggleStep(minutes)">
              <span class="i-lucide-x text-[10px]" />
            </button>
          </span>
        </div>
        <p class="text-[11px] text-n-slate-10">
          {{ t('AI_AGENTS.FOLLOW_UP.STEPS_HINT') }}
        </p>

        <label class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-11">
            {{ t('AI_AGENTS.FOLLOW_UP.RESOLVE_AT_END') }}
          </span>
          <input
            v-model="followUpForm.resolveAtEnd"
            type="checkbox"
            class="rounded border-n-weak"
          />
        </label>

        <!-- Último degrau: entrega a conversa em vez de mandar mais follow-up.
             Campo vazio = desligado, e o job segue como sempre foi. -->
        <div class="pt-3 border-t border-n-weak">
          <p class="text-xs font-medium text-n-slate-12">
            {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_TITLE') }}
          </p>
          <p class="mt-0.5 mb-2 text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_HINT') }}
          </p>
          <div class="flex items-center gap-2">
            <input
              v-model.number="followUpForm.escalateAfter"
              type="number"
              min="0"
              :placeholder="t('AI_AGENTS.FOLLOW_UP.ESCALATE_PLACEHOLDER')"
              class="w-24 px-2 py-1 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_MINUTES') }}
            </span>
            <select
              v-model="followUpForm.escalateKind"
              :disabled="!followUpForm.escalateAfter"
              class="flex-1 px-2 py-1 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12"
              @change="onEscalateKind"
            >
              <option value="sdr">
                {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_TO_SDR') }}
              </option>
              <option value="team">
                {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_TO_TEAM') }}
              </option>
              <option value="agent">
                {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_TO_AGENT') }}
              </option>
            </select>
          </div>

          <!-- Sem PARA QUEM, o escalonamento não acontecia e nada avisava. -->
          <template v-if="followUpForm.escalateAfter">
            <div v-if="followUpForm.escalateKind === 'team'" class="mt-2">
              <select
                v-model.number="followUpForm.escalateId"
                data-testid="followup-escalate-team"
                class="w-full px-2 py-1 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12"
              >
                <option :value="null">
                  {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_TEAM_PH') }}
                </option>
                <option v-for="team in teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
              <p v-if="!teams.length" class="mt-1 text-[11px] text-n-amber-11">
                {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_NO_TEAMS') }}
              </p>
            </div>

            <div v-else-if="followUpForm.escalateKind === 'agent'" class="mt-2">
              <select
                v-model.number="followUpForm.escalateId"
                data-testid="followup-escalate-agent"
                class="w-full px-2 py-1 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12"
              >
                <option :value="null">
                  {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_AGENT_PH') }}
                </option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <!-- Rodízio: pool opcional. Vazio = rodízio entre os atendentes da
                 caixa, preferindo quem tem o perfil SDR. -->
            <div v-else class="mt-2">
              <p class="mb-1 text-[11px] text-n-slate-10">
                {{ t('AI_AGENTS.FOLLOW_UP.ESCALATE_SDR_POOL_HINT') }}
              </p>
              <div
                class="flex flex-col gap-1 p-2 overflow-y-auto border rounded-lg max-h-32 border-n-weak"
              >
                <label
                  v-for="user in accountUsers"
                  :key="user.id"
                  class="flex items-center gap-2 text-xs text-n-slate-11"
                >
                  <input
                    type="checkbox"
                    :data-testid="`followup-sdr-${user.id}`"
                    :checked="followUpForm.sdrPool.includes(Number(user.id))"
                    class="rounded border-n-weak"
                    @change="toggleSdrPoolUser(user.id, $event.target.checked)"
                  />
                  <span>{{ user.name }}</span>
                </label>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Dialog>
</template>
